import React, { useEffect } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { BookOpenOutline, ClockOutline } from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import { Typography } from '@lyearn/ui';
import { Dialog } from '@/components/Modals';
import { EventSelectionInput } from '@/types/schema';

import useAssignmentStats from '../../hooks/useAssignmentStats';
import BlockedAssignmentModal from '../BlockedAssignmentModal';
import Loader from './Loader';

function ConfirmAssignmentDialog({
  onSubmit,
  onClose,
  scheduledDate,
  fetching,
  collectionIds,
  contentIds,
  learnerIds,
  userGroupIds,
  setFetchingStats,
  eventSelections,
  removedUserIds,
  importedUserGroupIds,
}: {
  onSubmit: () => void;
  onClose: () => void;
  collectionIds: string[];
  contentIds: string[];
  learnerIds: string[];
  userGroupIds: string[];
  eventSelections?: EventSelectionInput[] | null;
  scheduledDate?: string;
  fetching?: boolean;
  setFetchingStats: (value: boolean | undefined) => void;
  removedUserIds?: string[];
  importedUserGroupIds?: string[];
}) {
  const { t } = useTranslation();

  const {
    fetching: fetchingCount,
    contentsCount,
    learnersCount,
    canAssign,
    notVisibleContents,
  } = useAssignmentStats({
    filters: {
      collectionIds,
      contentIds,
      userIds: learnerIds,
      userGroupIds,
      eventSelections,
      removedUserIds,
      importedUserGroupIds,
    },
  });

  useEffect(() => setFetchingStats(fetchingCount), [fetchingCount, setFetchingStats]);

  if (fetchingCount) {
    return null;
  }

  if (!canAssign && notVisibleContents?.length) {
    return (
      <BlockedAssignmentModal
        fetching={fetchingCount}
        notVisibleContents={notVisibleContents}
        onClose={onClose}
      />
    );
  }

  return (
    <AlertDialog
      header={{
        title: t('AssignmentCreator:ConfirmAssignment'),
        description: '',
      }}
      primaryAction={{
        label: t('Common:Actions.Confirm'),
        onClick: onSubmit,
        loading: fetching,
        disabled: fetchingCount,
      }}
      onClose={onClose}>
      <Dialog.List
        className="mt-16 !px-8"
        items={[
          ...(fetchingCount
            ? [
                {
                  text: (
                    <Stack className="h-22">
                      <Loader />
                    </Stack>
                  ),
                },
              ]
            : [
                {
                  Icon: BookOpenOutline,
                  text: (
                    <Stack alignItems="center">
                      <Typography variant="heading-xxs">
                        {t('AssignmentCreator:ContentCount', { count: contentsCount })}
                      </Typography>
                      &nbsp;
                      <Typography variant="body-short-02">
                        {t('AssignmentCreator:WillBeAssignedTo')}
                      </Typography>
                      &nbsp;
                      <Typography variant="heading-xxs">
                        {t('Common:BulkExport.Learner', { count: learnersCount })}
                      </Typography>
                    </Stack>
                  ),
                },
              ]),
          ...(scheduledDate
            ? [
                {
                  Icon: ClockOutline,
                  text: (
                    <Stack alignItems="center">
                      <Typography variant="body-short-02">
                        {t('AssignmentCreator:AssignmentWillBeProcessedOn')}
                      </Typography>
                      &nbsp;
                      <Typography variant="heading-xxs">{scheduledDate}</Typography>
                    </Stack>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Dialog.Content className="!mx-8 !mb-0">
        <Typography variant="body-short-02">
          {t('AssignmentCreator:ConfirmAssignmentDescription')}
        </Typography>
      </Dialog.Content>
    </AlertDialog>
  );
}

export default ConfirmAssignmentDialog;
